import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  LineItem,
  EquipmentItem,
  PVWireItem,
  PagePathsMap,
  AppMode,
  BoundingBox,
  KickoffRecord,
  MaterialTotals,
  Point,
  ActiveAppView,
  MaterialItem,
  NecCalculatorConfig,
} from '../types';
import { calculateTotals } from '../services/calculations';
import { generateBillOfMaterials, DEFAULT_NEC_CONFIG } from '../services/necCalculator';
import {
  uploadAndProcessPdf,
  fetchPdfBlob,
  updateKickoffPaths,
} from '../services/api';
import { debounce } from '../utils/helpers';

interface StatusState {
  message: string;
  type: 'normal' | 'success' | 'error';
  loading: boolean;
}

interface AppContextType {
  // File & Document
  currentDocumentId: string | null;
  selectedFileName: string;
  currentFileBlob: Blob | null;
  loadPdfBlob: (blob: Blob) => void;

  // PDF Navigation & Scaling
  pdfScale: number;
  setPdfScale: React.Dispatch<React.SetStateAction<number>>;
  currentPageIndex: number;
  setCurrentPageIndex: (page: number) => void;
  numPages: number;
  setNumPages: (count: number) => void;

  // Modes & Tools
  appMode: AppMode;
  setAppMode: (mode: AppMode) => void;
  pixelsPerFoot: number | null;
  setPixelsPerFoot: (ppf: number | null) => void;

  // Drawing & Paths
  pagePathsMap: PagePathsMap;
  addDrawnString: (name: string, path: Point[]) => void;
  removeDrawnString: (page: number, index: number) => void;
  editDrawnStringLength: (page: number, index: number, overrideLen: number | null) => void;
  undoLastWire: () => void;
  hideAllWiresOnCurrentPage: () => void;

  // Takeoff Data
  lineItems: LineItem[];
  equipmentItems: EquipmentItem[];
  pvWireItems: PVWireItem[];
  updateLineItem: (rowIdx: number, key: string, value: string) => void;

  // Derived Totals
  materialTotals: MaterialTotals;
  drawnWireTotals: {
    positive: number;
    negative: number;
    other: number;
    total: number;
  };

  // Highlighting
  activeBbox: BoundingBox | null;
  activeRowIndex: number | null;
  highlightItem: (bbox?: BoundingBox | null, rowIdx?: number | null) => void;

  // Layout & UI
  singleViewMode: boolean;
  toggleSingleView: () => void;
  isHistoryOpen: boolean;
  setIsHistoryOpen: (open: boolean) => void;
  status: StatusState;
  setStatus: (message: string, type?: 'normal' | 'success' | 'error', loading?: boolean) => void;

  // Screenshots & debug
  screenshots: {
    wireScheduleUrl?: string;
    equipmentScheduleUrl?: string;
    stringLayoutUrls?: string[];
  };

  // Active Application View (Takeoff, 3D One-Line, Material List)
  activeView: ActiveAppView;
  setActiveView: (view: ActiveAppView) => void;

  // Material List (BOM) & NEC Calculator
  bomItems: MaterialItem[];
  setBomItems: React.Dispatch<React.SetStateAction<MaterialItem[]>>;
  addBomItem: (item: Omit<MaterialItem, 'id'>) => void;
  updateBomItem: (id: string, updates: Partial<MaterialItem>) => void;
  removeBomItem: (id: string) => void;
  necConfig: NecCalculatorConfig;
  setNecConfig: React.Dispatch<React.SetStateAction<NecCalculatorConfig>>;
  recalculateBom: () => void;

  // Actions
  uploadFile: (file: File) => Promise<void>;
  loadHistoricalData: (record: KickoffRecord) => Promise<void>;
  resetUI: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const getDefaultPdfScale = () => {
  if (typeof window === 'undefined') return 0.5;
  return window.innerWidth < 768 ? 0.6 : window.innerWidth < 1100 ? 0.5 : 0.4;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // File & Document state
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [currentFileBlob, setCurrentFileBlob] = useState<Blob | null>(null);

  // PDF Navigation & Scaling
  const [pdfScale, setPdfScale] = useState<number>(getDefaultPdfScale);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [numPages, setNumPages] = useState<number>(1);

  // Modes & Calibration
  const [appMode, setAppMode] = useState<AppMode>('SELECT');
  const [pixelsPerFoot, setPixelsPerFoot] = useState<number | null>(null);

  // Paths
  const [pagePathsMap, setPagePathsMap] = useState<PagePathsMap>({});

  // Extracted Data
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);
  const [pvWireItems, setPvWireItems] = useState<PVWireItem[]>([]);

  // Screenshots
  const [screenshots, setScreenshots] = useState<{
    wireScheduleUrl?: string;
    equipmentScheduleUrl?: string;
    stringLayoutUrls?: string[];
  }>({});

  // Highlight
  const [activeBbox, setActiveBbox] = useState<BoundingBox | null>(null);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  // Active View (Takeoff, 3D One-Line, Material List)
  const [activeView, setActiveView] = useState<ActiveAppView>('TAKEOFF');

  // NEC Calculator & BOM State
  const [necConfig, setNecConfig] = useState<NecCalculatorConfig>(DEFAULT_NEC_CONFIG);
  const [bomItems, setBomItems] = useState<MaterialItem[]>([]);

  // Layout & UI
  const [singleViewMode, setSingleViewMode] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [status, setStatusState] = useState<StatusState>({
    message: '',
    type: 'normal',
    loading: false,
  });

  const recalculateBom = useCallback(() => {
    if (lineItems.length > 0) {
      const generated = generateBillOfMaterials(lineItems, equipmentItems, pvWireItems, necConfig);
      setBomItems(generated);
    } else {
      const mockLines: LineItem[] = [
        { Circuit_Origin: 'Inverter INV-1', Circuit_Destination: 'AC Disconnect SW-1', Trade_Size: '#2 AWG Cu', Qty_Wire: 3, Conduit_Size: '1-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#6 AWG', Max_Approx_Length: 20, Qty_Parallel: 1 },
        { Circuit_Origin: 'Inverter INV-2', Circuit_Destination: 'AC Disconnect SW-2', Trade_Size: '#2 AWG Cu', Qty_Wire: 3, Conduit_Size: '1-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#6 AWG', Max_Approx_Length: 20, Qty_Parallel: 1 },
        { Circuit_Origin: 'AC Disconnect SW-1', Circuit_Destination: 'AC Combiner Panel', Trade_Size: '#2 AWG Cu', Qty_Wire: 3, Conduit_Size: '1-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#6 AWG', Max_Approx_Length: 25, Qty_Parallel: 1 },
        { Circuit_Origin: 'AC Disconnect SW-2', Circuit_Destination: 'AC Combiner Panel', Trade_Size: '#2 AWG Cu', Qty_Wire: 3, Conduit_Size: '1-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#6 AWG', Max_Approx_Length: 25, Qty_Parallel: 1 },
        { Circuit_Origin: 'AC Combiner Panel', Circuit_Destination: 'Isolation Transformer', Trade_Size: '250 kcmil Cu', Qty_Wire: 3, Conduit_Size: '2-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#4 AWG', Max_Approx_Length: 35, Qty_Parallel: 1 },
        { Circuit_Origin: 'Isolation Transformer', Circuit_Destination: 'Production Meter', Trade_Size: '250 kcmil Cu', Qty_Wire: 4, Conduit_Size: '2-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#4 AWG', Max_Approx_Length: 30, Qty_Parallel: 1 },
        { Circuit_Origin: 'Production Meter', Circuit_Destination: 'Main Switchboard', Trade_Size: '250 kcmil Cu', Qty_Wire: 4, Conduit_Size: '2-1/2"', Conduit_Type: 'EMT', Equipment_Grounding_Conductor_Size: '#4 AWG', Max_Approx_Length: 25, Qty_Parallel: 1 },
      ];
      const mockEquipment: EquipmentItem[] = [
        { Equipment: 'Inverter INV-1 (75kW)', Enclosure: 'NEMA 4X', Rating: '75kW 480V', Quantity: 1, Status: 'NEW' },
        { Equipment: 'Inverter INV-2 (75kW)', Enclosure: 'NEMA 4X', Rating: '75kW 480V', Quantity: 1, Status: 'NEW' },
        { Equipment: 'AC Disconnect SW-1', Enclosure: 'NEMA 3R', Rating: '100A 600V', Quantity: 1, Status: 'NEW' },
        { Equipment: 'AC Disconnect SW-2', Enclosure: 'NEMA 3R', Rating: '100A 600V', Quantity: 1, Status: 'NEW' },
        { Equipment: 'AC Combiner Panel', Enclosure: 'NEMA 3R', Rating: '250A 480V', Quantity: 1, Status: 'NEW' },
        { Equipment: 'Isolation Transformer', Enclosure: 'NEMA 3R', Rating: '175kVA', Quantity: 1, Status: 'NEW' },
        { Equipment: 'Production Meter', Enclosure: 'NEMA 3R', Rating: '400A Class', Quantity: 1, Status: 'NEW' },
        { Equipment: 'Main Switchboard MSB-1', Enclosure: 'NEMA 1', Rating: '1600A Bus / 250A Solar', Quantity: 1, Status: 'EXISTING' },
      ];
      const generated = generateBillOfMaterials(mockLines, mockEquipment, [], necConfig);
      setBomItems(generated);
    }
  }, [lineItems, equipmentItems, pvWireItems, necConfig]);

  useEffect(() => {
    recalculateBom();
  }, [recalculateBom]);

  const addBomItem = useCallback((item: Omit<MaterialItem, 'id'>) => {
    const newItem: MaterialItem = {
      ...item,
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    };
    setBomItems((prev) => [newItem, ...prev]);
  }, []);

  const updateBomItem = useCallback((id: string, updates: Partial<MaterialItem>) => {
    setBomItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  }, []);

  const removeBomItem = useCallback((id: string) => {
    setBomItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const setStatus = useCallback(
    (message: string, type: 'normal' | 'success' | 'error' = 'normal', loading = false) => {
      setStatusState({ message, type, loading });
    },
    []
  );

  const toggleSingleView = useCallback(() => {
    setSingleViewMode((prev) => !prev);
  }, []);

  const highlightItem = useCallback(
    (bbox?: BoundingBox | null, rowIdx?: number | null) => {
      setActiveBbox(bbox || null);
      setActiveRowIndex(rowIdx !== undefined ? rowIdx : null);
    },
    []
  );

  const loadPdfBlob = useCallback((blob: Blob) => {
    setCurrentFileBlob(blob);
    setCurrentPageIndex(0);
  }, []);

  // Debounced auto-save paths to DB
  const docIdRef = useRef<string | null>(currentDocumentId);
  docIdRef.current = currentDocumentId;

  const debouncedSavePathsRef = useRef(
    debounce((docId: string, paths: PagePathsMap, ppf: number | null) => {
      if (!docId) return;
      updateKickoffPaths(docId, paths, ppf).catch((err) => {
        console.error('Auto-save error:', err);
      });
    }, 1000)
  );

  const triggerSavePaths = useCallback(
    (newPaths: PagePathsMap, ppf: number | null) => {
      if (docIdRef.current) {
        debouncedSavePathsRef.current(docIdRef.current, newPaths, ppf);
      }
    },
    []
  );

  const addDrawnString = useCallback(
    (name: string, path: Point[]) => {
      setPagePathsMap((prev) => {
        const pageItems = prev[currentPageIndex] ? [...prev[currentPageIndex]] : [];
        pageItems.push({
          name,
          path: [...path],
          hidden: false,
          overrideLen: null,
        });
        const updated = { ...prev, [currentPageIndex]: pageItems };
        triggerSavePaths(updated, pixelsPerFoot);
        return updated;
      });
    },
    [currentPageIndex, pixelsPerFoot, triggerSavePaths]
  );

  const removeDrawnString = useCallback(
    (page: number, index: number) => {
      setPagePathsMap((prev) => {
        if (!prev[page]) return prev;
        const pageItems = [...prev[page]];
        pageItems.splice(index, 1);
        const updated = { ...prev, [page]: pageItems };
        triggerSavePaths(updated, pixelsPerFoot);
        return updated;
      });
    },
    [pixelsPerFoot, triggerSavePaths]
  );

  const editDrawnStringLength = useCallback(
    (page: number, index: number, overrideLen: number | null) => {
      setPagePathsMap((prev) => {
        if (!prev[page] || !prev[page][index]) return prev;
        const pageItems = [...prev[page]];
        pageItems[index] = {
          ...pageItems[index],
          overrideLen,
        };
        const updated = { ...prev, [page]: pageItems };
        triggerSavePaths(updated, pixelsPerFoot);
        return updated;
      });
    },
    [pixelsPerFoot, triggerSavePaths]
  );

  const undoLastWire = useCallback(() => {
    setPagePathsMap((prev) => {
      const pageItems = prev[currentPageIndex];
      if (!pageItems || pageItems.length === 0) return prev;
      const updatedItems = pageItems.slice(0, -1);
      const updated = { ...prev, [currentPageIndex]: updatedItems };
      triggerSavePaths(updated, pixelsPerFoot);
      return updated;
    });
  }, [currentPageIndex, pixelsPerFoot, triggerSavePaths]);

  const hideAllWiresOnCurrentPage = useCallback(() => {
    setPagePathsMap((prev) => {
      const pageItems = prev[currentPageIndex];
      if (!pageItems) return prev;
      const updatedItems = pageItems.map((item) => ({ ...item, hidden: true }));
      const updated = { ...prev, [currentPageIndex]: updatedItems };
      triggerSavePaths(updated, pixelsPerFoot);
      return updated;
    });
  }, [currentPageIndex, pixelsPerFoot, triggerSavePaths]);

  const updateLineItem = useCallback((rowIdx: number, key: string, value: string) => {
    setLineItems((prev) => {
      const copy = [...prev];
      if (copy[rowIdx]) {
        copy[rowIdx] = { ...copy[rowIdx], [key]: value };
      }
      return copy;
    });
  }, []);

  // Reactive Derived Material Totals
  const materialTotals = useMemo(() => {
    return calculateTotals(lineItems, pvWireItems);
  }, [lineItems, pvWireItems]);

  // Reactive Derived Drawn Wire Totals
  const drawnWireTotals = useMemo(() => {
    let positive = 0;
    let negative = 0;
    let other = 0;

    Object.values(pagePathsMap).forEach((pageItems) => {
      pageItems.forEach((item) => {
        let itemFeet = 0;
        if (item.overrideLen !== null && item.overrideLen !== undefined) {
          itemFeet = parseFloat(String(item.overrideLen)) || 0;
        } else if (pixelsPerFoot) {
          const path = item.path || [];
          let itemUnzoomedPixels = 0;
          for (let i = 1; i < path.length; i++) {
            if (!path[i] || !path[i - 1]) continue;
            const dx = path[i].x - path[i - 1].x;
            const dy = path[i].y - path[i - 1].y;
            itemUnzoomedPixels += Math.sqrt(dx * dx + dy * dy);
          }
          itemFeet = itemUnzoomedPixels / pixelsPerFoot;
        }

        const name = (item.name || '').toLowerCase();
        if (name.includes('positive')) {
          positive += itemFeet;
        } else if (name.includes('negative')) {
          negative += itemFeet;
        } else {
          other += itemFeet;
        }
      });
    });

    return {
      positive,
      negative,
      other,
      total: positive + negative + other,
    };
  }, [pagePathsMap, pixelsPerFoot]);

  const resetUI = useCallback(() => {
    setCurrentDocumentId(null);
    setSelectedFileName('');
    setCurrentFileBlob(null);
    setCurrentPageIndex(0);
    setNumPages(1);
    setAppMode('SELECT');
    setPixelsPerFoot(null);
    setPagePathsMap({});
    setLineItems([]);
    setEquipmentItems([]);
    setPvWireItems([]);
    setScreenshots({});
    setActiveBbox(null);
    setActiveRowIndex(null);
    setStatus('', 'normal', false);
  }, [setStatus]);

  const uploadFile = useCallback(
    async (file: File) => {
      resetUI();
      setSelectedFileName(file.name);
      setStatus('Uploading and processing... This may take a moment.', 'normal', true);

      try {
        const result = await uploadAndProcessPdf(file);
        setCurrentDocumentId(result.id);
        setSelectedFileName(result.fileName || file.name);

        // Filter line items matching legacy logic
        const filteredLines = (result.lineItems || []).filter((item) => {
          const ac_dc = (item['AC_DC'] || '').toLowerCase();
          const origin = (item['Circuit_Origin'] || '').toLowerCase();
          const dest = (item['Circuit_Destination'] || '').toLowerCase();
          return !(ac_dc.includes('dc') || (origin.includes('module') && dest.includes('inverter')));
        });

        setLineItems(filteredLines);
        setEquipmentItems(result.equipmentItems || []);
        setPvWireItems(result.pvWireItems || []);
        setPagePathsMap(result.pagePathsMap || {});
        setPixelsPerFoot(result.pixelsPerFoot || null);

        setScreenshots({
          wireScheduleUrl: result.wireScheduleScreenshotUrl,
          equipmentScheduleUrl: result.equipmentScheduleScreenshotUrl,
          stringLayoutUrls: result.stringLayoutScreenshotUrls,
        });

        // Load the PDF blob
        if (result.pdfStorageUrl) {
          try {
            const blob = await fetchPdfBlob(result.id);
            setCurrentFileBlob(blob);
          } catch {
            // Fallback to local uploaded file
            setCurrentFileBlob(file);
          }
        } else {
          setCurrentFileBlob(file);
        }

        const itemsFound =
          filteredLines.length > 0 ||
          (result.equipmentItems && result.equipmentItems.length > 0) ||
          (result.pvWireItems && result.pvWireItems.length > 0);

        if (itemsFound) {
          setStatus('Success! Extracted data below.', 'success', false);
        } else {
          setStatus('Processing complete, but no line items were found.', 'normal', false);
        }
      } catch (err: any) {
        console.error('Processing error:', err);
        setStatus(err?.message || 'Processing failed. Please try again.', 'error', false);
      }
    },
    [resetUI, setStatus]
  );

  const loadHistoricalData = useCallback(
    async (record: KickoffRecord) => {
      resetUI();
      setCurrentDocumentId(record.id);
      setSelectedFileName(record.fileName || 'Historical Plan');

      const filteredLines = (record.lineItems || []).filter((item) => {
        const ac_dc = (item['AC_DC'] || '').toLowerCase();
        const origin = (item['Circuit_Origin'] || '').toLowerCase();
        const dest = (item['Circuit_Destination'] || '').toLowerCase();
        return !(ac_dc.includes('dc') || (origin.includes('module') && dest.includes('inverter')));
      });

      setLineItems(filteredLines);
      setEquipmentItems(record.equipmentItems || []);
      setPvWireItems(record.pvWireItems || []);
      setPagePathsMap(record.pagePathsMap || {});
      setPixelsPerFoot(record.pixelsPerFoot || null);

      setScreenshots({
        wireScheduleUrl: record.wireScheduleScreenshotUrl,
        equipmentScheduleUrl: record.equipmentScheduleScreenshotUrl,
        stringLayoutUrls: record.stringLayoutScreenshotUrls,
      });

      setStatus('Downloading historical PDF...', 'normal', true);

      if (record.pdfStorageUrl) {
        try {
          const blob = await fetchPdfBlob(record.id);
          setCurrentFileBlob(blob);
          setStatus(`Loaded "${record.fileName || 'Plan'}" from history.`, 'success', false);
        } catch (err) {
          console.error('Failed to load historical PDF:', err);
          setStatus('Failed to load PDF for historical record.', 'error', false);
        }
      } else {
        setStatus(`Loaded record "${record.fileName}" (No PDF attached).`, 'normal', false);
      }
    },
    [resetUI, setStatus]
  );

  return (
    <AppContext.Provider
      value={{
        currentDocumentId,
        selectedFileName,
        currentFileBlob,
        loadPdfBlob,
        pdfScale,
        setPdfScale,
        currentPageIndex,
        setCurrentPageIndex,
        numPages,
        setNumPages,
        appMode,
        setAppMode,
        pixelsPerFoot,
        setPixelsPerFoot,
        pagePathsMap,
        addDrawnString,
        removeDrawnString,
        editDrawnStringLength,
        undoLastWire,
        hideAllWiresOnCurrentPage,
        lineItems,
        equipmentItems,
        pvWireItems,
        updateLineItem,
        materialTotals,
        drawnWireTotals,
        activeBbox,
        activeRowIndex,
        highlightItem,
        singleViewMode,
        toggleSingleView,
        isHistoryOpen,
        setIsHistoryOpen,
        status,
        setStatus,
        screenshots,
        activeView,
        setActiveView,
        bomItems,
        setBomItems,
        addBomItem,
        updateBomItem,
        removeBomItem,
        necConfig,
        setNecConfig,
        recalculateBom,
        uploadFile,
        loadHistoricalData,
        resetUI,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
